import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AssetHierarchyService } from "../services/asset-hierarchy.service";
import {
  Organization,
  BusinessUnit,
  AssetGroup,
  Plant,
} from "../interfaces/asset-hierarchy/asset-hierarchy.models";

@Component({
    selector: "app-asset-hierarchy",
    templateUrl: "./asset-hierarchy.component.html",
    styleUrls: ["./asset-hierarchy.component.css"],
    standalone: false
})
export class AssetHierarchyComponent implements OnInit {
  hierarchyForm: FormGroup;
  addNewForm: FormGroup;

  organizations: Organization[] = [];
  businessUnits: BusinessUnit[] = [];
  assetGroups: AssetGroup[] = [];
  plants: Plant[] = [];

  // Error handling properties
  organizationError: string | null = null;
  businessUnitError: string | null = null;
  assetGroupError: string | null = null;
  plantError: string | null = null;

  // Validation lists for validation component
  organizationValidationList: string[] = [];
  businessUnitValidationList: string[] = [];
  assetGroupValidationList: string[] = [];
  plantValidationList: string[] = [];

  // Loading states for spinner
  isLoadingOrganizations = false;
  isLoadingBusinessUnits = false;
  isLoadingAssetGroups = false;
  isLoadingPlants = false;
  isCreatingOrganization = false;
  isCreatingBusinessUnit = false;
  isCreatingAssetGroup = false;
  isCreatingPlant = false;

  constructor(
    private fb: FormBuilder,
    private assetHierarchyService: AssetHierarchyService
  ) {
    this.hierarchyForm = this.fb.group({
      selectedOrganizationId: [null],
      selectedBusinessUnitId: [{ value: null, disabled: true }],
      selectedAssetGroupId: [{ value: null, disabled: true }],
      selectedPlantId: [{ value: null, disabled: true }],
    });

    this.addNewForm = this.fb.group({
      newOrganization: ["", [Validators.required, Validators.minLength(1)]],
      newBusinessUnit: ["", [Validators.required, Validators.minLength(1)]],
      newAssetGroup: ["", [Validators.required, Validators.minLength(1)]],
      newPlant: ["", [Validators.required, Validators.minLength(1)]],
    });
  }

  ngOnInit(): void {
    this.setupFormSubscriptions();
    this.loadOrganizations();
  }

  private setupFormSubscriptions(): void {
    this.hierarchyForm
      .get("selectedOrganizationId")
      ?.valueChanges.subscribe((value) => {
        if (value) {
          this.onOrganizationChange(value);
        }
      });

    this.hierarchyForm
      .get("selectedBusinessUnitId")
      ?.valueChanges.subscribe((value) => {
        if (value) {
          this.onBusinessUnitChange(value);
        }
      });

    this.hierarchyForm
      .get("selectedAssetGroupId")
      ?.valueChanges.subscribe((value) => {
        if (value) {
          this.onAssetGroupChange(value);
        }
      });
  }

  loadOrganizations(): void {
    this.organizationError = null;
    this.isLoadingOrganizations = true;
    this.assetHierarchyService.getOrganizations().subscribe({
      next: (organizations) => {
        this.organizations = organizations;
        if (organizations.length > 0) {
          this.hierarchyForm.patchValue({
            selectedOrganizationId: organizations[0].id,
          });
        }
        this.isLoadingOrganizations = false;
      },
      error: (error) => {
        this.organizationError =
          "Failed to load organizations. Please try again.";
        console.error("Error loading organizations:", error);
        this.isLoadingOrganizations = false;
      },
    });
  }

  onOrganizationChange(organizationId: number): void {
    this.businessUnits = [];
    this.assetGroups = [];
    this.plants = [];
    this.businessUnitError = null;
    this.assetGroupError = null;
    this.plantError = null;

    // Reset and disable dependent controls
    this.hierarchyForm.get("selectedBusinessUnitId")?.setValue(null);
    this.hierarchyForm.get("selectedBusinessUnitId")?.disable();
    this.hierarchyForm.get("selectedAssetGroupId")?.setValue(null);
    this.hierarchyForm.get("selectedAssetGroupId")?.disable();
    this.hierarchyForm.get("selectedPlantId")?.setValue(null);
    this.hierarchyForm.get("selectedPlantId")?.disable();

    this.isLoadingBusinessUnits = true;
    this.assetHierarchyService
      .getBusinessUnitsByOrganizationId(organizationId)
      .subscribe({
        next: (businessUnits) => {
          this.businessUnits = businessUnits;
          if (businessUnits.length > 0) {
            this.hierarchyForm.get("selectedBusinessUnitId")?.enable();
            this.hierarchyForm.patchValue({
              selectedBusinessUnitId: businessUnits[0].id,
            });
          }
          this.isLoadingBusinessUnits = false;
        },
        error: (error) => {
          this.businessUnitError =
            "Failed to load business units. Please try again.";
          console.error("Error loading business units:", error);
          this.isLoadingBusinessUnits = false;
        },
      });
  }

  onBusinessUnitChange(businessUnitId: number): void {
    this.assetGroups = [];
    this.plants = [];
    this.assetGroupError = null;
    this.plantError = null;

    // Reset and disable dependent controls
    this.hierarchyForm.get("selectedAssetGroupId")?.setValue(null);
    this.hierarchyForm.get("selectedAssetGroupId")?.disable();
    this.hierarchyForm.get("selectedPlantId")?.setValue(null);
    this.hierarchyForm.get("selectedPlantId")?.disable();

    this.isLoadingAssetGroups = true;
    this.assetHierarchyService
      .getAssetGroupsByBusinessUnitId(businessUnitId)
      .subscribe({
        next: (assetGroups) => {
          this.assetGroups = assetGroups;
          if (assetGroups.length > 0) {
            this.hierarchyForm.get("selectedAssetGroupId")?.enable();
            this.hierarchyForm.patchValue({
              selectedAssetGroupId: assetGroups[0].id,
            });
          }
          this.isLoadingAssetGroups = false;
        },
        error: (error) => {
          // Only show error to user if it's not a 404 (no records found)
          if (error.status !== 404) {
            this.assetGroupError =
              "Failed to load asset groups. Please try again.";
          }
          console.error("Error loading asset groups:", error);
          this.isLoadingAssetGroups = false;
        },
      });
  }

  onAssetGroupChange(assetGroupId: number): void {
    this.plants = [];
    this.plantError = null;

    // Reset and disable dependent control
    this.hierarchyForm.get("selectedPlantId")?.setValue(null);
    this.hierarchyForm.get("selectedPlantId")?.disable();

    this.isLoadingPlants = true;
    this.assetHierarchyService.getPlantsByAssetGroupId(assetGroupId).subscribe({
      next: (plants) => {
        this.plants = plants;
        if (plants.length > 0) {
          this.hierarchyForm.get("selectedPlantId")?.enable();
          this.hierarchyForm.patchValue({
            selectedPlantId: plants[0].id,
          });
        }
        this.isLoadingPlants = false;
      },
      error: (error) => {
        // Only show error to user if it's not a 404 (no records found)
        if (error.status !== 404) {
          this.plantError = "Failed to load plants. Please try again.";
        }
        console.error("Error loading plants:", error);
        this.isLoadingPlants = false;
      },
    });
  }

  addNewOrganization(): void {
    this.organizationValidationList = [];
    const organizationControl = this.addNewForm.get("newOrganization");

    if (organizationControl?.invalid) {
      organizationControl.markAsTouched();
      this.organizationValidationList.push("Organization name is required.");
      return;
    }

    const newOrganization = organizationControl?.value?.trim();
    if (!newOrganization) {
      this.organizationValidationList.push(
        "Organization name cannot be empty."
      );
      return;
    }

    this.isCreatingOrganization = true;
    this.assetHierarchyService.createOrganization(newOrganization).subscribe({
      next: (organization) => {
        this.organizations.push(organization);
        this.hierarchyForm.patchValue({
          selectedOrganizationId: organization.id,
        });
        this.addNewForm.patchValue({ newOrganization: "" });
        organizationControl.markAsUntouched();
        this.isCreatingOrganization = false;
      },
      error: (error) => {
        console.error("Error creating organization:", error);
        this.organizationValidationList.push(
          "Failed to create organization. Please try again."
        );
        this.isCreatingOrganization = false;
      },
    });
  }

  addNewBusinessUnit(): void {
    this.businessUnitValidationList = [];
    const businessUnitControl = this.addNewForm.get("newBusinessUnit");
    const selectedOrgId = this.hierarchyForm.get(
      "selectedOrganizationId"
    )?.value;

    if (!selectedOrgId) {
      this.businessUnitValidationList.push(
        "Please select an organization first."
      );
      return;
    }

    if (businessUnitControl?.invalid) {
      businessUnitControl.markAsTouched();
      this.businessUnitValidationList.push("Business unit name is required.");
      return;
    }

    const newBusinessUnit = businessUnitControl?.value?.trim();
    if (!newBusinessUnit) {
      this.businessUnitValidationList.push(
        "Business unit name cannot be empty."
      );
      return;
    }

    this.isCreatingBusinessUnit = true;
    this.assetHierarchyService
      .createBusinessUnit(newBusinessUnit, selectedOrgId)
      .subscribe({
        next: (businessUnit) => {
          this.businessUnits.push(businessUnit);
          this.hierarchyForm.get("selectedBusinessUnitId")?.enable();
          this.hierarchyForm.patchValue({
            selectedBusinessUnitId: businessUnit.id,
          });
          this.addNewForm.patchValue({ newBusinessUnit: "" });
          businessUnitControl.markAsUntouched();
          this.isCreatingBusinessUnit = false;
        },
        error: (error) => {
          console.error("Error creating business unit:", error);
          this.businessUnitValidationList.push(
            "Failed to create business unit. Please try again."
          );
          this.isCreatingBusinessUnit = false;
        },
      });
  }

  addNewAssetGroup(): void {
    this.assetGroupValidationList = [];
    const assetGroupControl = this.addNewForm.get("newAssetGroup");
    const selectedBusinessUnitId = this.hierarchyForm.get(
      "selectedBusinessUnitId"
    )?.value;

    if (!selectedBusinessUnitId) {
      this.assetGroupValidationList.push(
        "Please select a business unit first."
      );
      return;
    }

    if (assetGroupControl?.invalid) {
      assetGroupControl.markAsTouched();
      this.assetGroupValidationList.push("Asset group name is required.");
      return;
    }

    const newAssetGroup = assetGroupControl?.value?.trim();
    if (!newAssetGroup) {
      this.assetGroupValidationList.push("Asset group name cannot be empty.");
      return;
    }

    this.isCreatingAssetGroup = true;
    this.assetHierarchyService
      .createAssetGroup(newAssetGroup, selectedBusinessUnitId)
      .subscribe({
        next: (assetGroup) => {
          this.assetGroups.push(assetGroup);
          this.hierarchyForm.get("selectedAssetGroupId")?.enable();
          this.hierarchyForm.patchValue({
            selectedAssetGroupId: assetGroup.id,
          });
          this.addNewForm.patchValue({ newAssetGroup: "" });
          assetGroupControl.markAsUntouched();
          this.isCreatingAssetGroup = false;
        },
        error: (error) => {
          console.error("Error creating asset group:", error);
          this.assetGroupValidationList.push(
            "Failed to create asset group. Please try again."
          );
          this.isCreatingAssetGroup = false;
        },
      });
  }

  addNewPlant(): void {
    this.plantValidationList = [];
    const plantControl = this.addNewForm.get("newPlant");
    const selectedAssetGroupId = this.hierarchyForm.get(
      "selectedAssetGroupId"
    )?.value;

    if (!selectedAssetGroupId) {
      this.plantValidationList.push("Please select an asset group first.");
      return;
    }

    if (plantControl?.invalid) {
      plantControl.markAsTouched();
      this.plantValidationList.push("Plant name is required.");
      return;
    }

    const newPlant = plantControl?.value?.trim();
    if (!newPlant) {
      this.plantValidationList.push("Plant name cannot be empty.");
      return;
    }

    this.isCreatingPlant = true;
    this.assetHierarchyService
      .createPlant(newPlant, selectedAssetGroupId)
      .subscribe({
        next: (plant) => {
          this.plants.push(plant);
          this.hierarchyForm.get("selectedPlantId")?.enable();
          this.hierarchyForm.patchValue({
            selectedPlantId: plant.id,
          });
          this.addNewForm.patchValue({ newPlant: "" });
          plantControl.markAsUntouched();
          this.isCreatingPlant = false;
        },
        error: (error) => {
          console.error("Error creating plant:", error);
          this.plantValidationList.push(
            "Failed to create plant. Please try again."
          );
          this.isCreatingPlant = false;
        },
      });
  }
}
